import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { formatCurrency } from '@/utils/Formatters/formatCurrency';
import { GATOR_PROTECT_ID } from '../GatorProtect.enum';
import {
  EMPTY_GATOR_PROTECT_PRODUCT,
  type CheckoutProductPricingCycleRow,
  type ConsultedGatorProduct,
  type GatorProtectProductId,
} from './gatorProtectConsultedProduct.types';

type PartialProductsReduxState = Partial<{
  products?: {
    consultedProducts?: ConsultedGatorProduct[];
  };
}>;

/** WHMCS billing cycle keys used for Gator Protect PRO upgrade modal. */
export const GATOR_PROTECT_PRO_SUPPORTED_BILLING_CYCLES = [
  'annually',
  'triennially',
  'monthly',
] as const;

export type GatorProtectProBillingCycle =
  (typeof GATOR_PROTECT_PRO_SUPPORTED_BILLING_CYCLES)[number];

const CYCLE_DISPLAY_ORDER: readonly GatorProtectProBillingCycle[] = [
  'annually',
  'triennially',
  'monthly',
];

export type GatorProtectProPlanCycle = {
  formattedPrice: string | null;
  discountPercent: number;
  /** Business rule: only the annual plan is marked best today */
  isBestOption?: boolean;
};

export type UseGatorProtectProPlanResult = {
  monthly: GatorProtectProPlanCycle | null;
  annual: GatorProtectProPlanCycle | null;
  triennial: GatorProtectProPlanCycle | null;
  availableCycles: GatorProtectProBillingCycle[];
  isLoadingPlanData: boolean;
};

function normalizeCycleToken(raw: string): string {
  return String(raw).trim().toLowerCase();
}

function isCycleAllowedByProduct(
  cycleKey: GatorProtectProBillingCycle,
  productCycles: string[] | undefined,
): boolean {
  if (productCycles == null || productCycles.length === 0) {
    return true;
  }
  return productCycles.some(c => normalizeCycleToken(c) === cycleKey);
}

/**
 * Normalizes WHMCS / checkout discount percentage fields (`cyclesDiscount.*.percentageCycle`, etc.).
 */
export function parseDiscountPercent(
  raw: number | string | null | undefined,
): number {
  if (raw == null || raw === '') {
    return 0;
  }
  const n = typeof raw === 'string' ? Number.parseFloat(raw) : Number(raw);
  return Number.isFinite(n) && !Number.isNaN(n) ? n : 0;
}

/**
 * Formats a WHMCS checkout cycle amount for display, or returns null when missing / invalid / unavailable (e.g. `-1.00`).
 * @param raw - Cycle string or number from `prices.pricing.*`
 * @returns Formatted currency string, or null
 */
export function formatPricingCycle(
  raw: string | number | undefined | null,
): string | null {
  if (raw == null || raw === '') {
    return null;
  }
  const asNumber = typeof raw === 'string' ? Number.parseFloat(raw) : Number(raw);
  if (Number.isNaN(asNumber) || asNumber < 0) {
    return null;
  }
  return formatCurrency(raw);
}

function rawPriceForCycle(
  pricing: CheckoutProductPricingCycleRow | undefined,
  cycleKey: GatorProtectProBillingCycle,
): string | undefined {
  if (!pricing) {
    return undefined;
  }
  if (cycleKey === 'monthly') {
    return pricing.monthly;
  }
  if (cycleKey === 'annually') {
    return pricing.annually;
  }
  return pricing.triennially;
}

function buildPlanCycle(
  cycleKey: GatorProtectProBillingCycle,
  product: ConsultedGatorProduct,
  markBest: boolean,
): GatorProtectProPlanCycle | null {
  const pricing = product.prices?.pricing;
  const formattedPrice = formatPricingCycle(
    rawPriceForCycle(pricing, cycleKey),
  );
  if (formattedPrice == null) {
    return null;
  }
  const discountPercent = parseDiscountPercent(
    product.cyclesDiscount?.[cycleKey]?.percentageCycle,
  );
  if (markBest) {
    return { formattedPrice, discountPercent, isBestOption: true };
  }
  return { formattedPrice, discountPercent };
}

/**
 * Reads Gator Protect PRO checkout pricing from Redux and exposes per-cycle amounts and discounts.
 * Does not dispatch `getProductById`; the page or another layout hook must trigger the fetch.
 */
export function useGatorProtectProPlan(
  productId: GatorProtectProductId = GATOR_PROTECT_ID.PRO,
): UseGatorProtectProPlanResult {
  const consultedProducts = useSelector(
    (state: PartialProductsReduxState) => state.products?.consultedProducts,
  );
  const found = consultedProducts?.find((item: ConsultedGatorProduct) => item.id === productId);
  const proProduct = found ?? EMPTY_GATOR_PROTECT_PRODUCT;

  return useMemo((): UseGatorProtectProPlanResult => {
    if (Array.isArray(proProduct)) {
      return {
        monthly: null,
        annual: null,
        triennial: null,
        availableCycles: [],
        isLoadingPlanData: true,
      };
    }

    const productCycles = proProduct.cycles;
    const pricingRow = proProduct.prices?.pricing;

    const availableCycles = CYCLE_DISPLAY_ORDER.filter((cycleKey) => {
      if (!isCycleAllowedByProduct(cycleKey, productCycles)) {
        return false;
      }
      return formatPricingCycle(rawPriceForCycle(pricingRow, cycleKey)) != null;
    });

    const monthly = availableCycles.includes('monthly')
      ? buildPlanCycle('monthly', proProduct, false)
      : null;
    const annual = availableCycles.includes('annually')
      ? buildPlanCycle('annually', proProduct, true)
      : null;
    const triennial = availableCycles.includes('triennially')
      ? buildPlanCycle('triennially', proProduct, false)
      : null;

    return {
      monthly,
      annual,
      triennial,
      availableCycles,
      isLoadingPlanData: false,
    };
  }, [proProduct]);
}
